import type { BillOut, ItemDiscountLineIn } from "../../../../../api/bills";

/** Бързите проценти — в общия избор горе и в менюто на всеки ред. */
export const QUICK_PERCENTS = [5, 10, 15, 20, 25, 50];

/** Един ценови ред от сметката — продуктът или някоя от добавките му. */
export interface PricedLine {
  price: number;
  quantity: number;
  /** Процентът на реда в момента — от отстъпка или изведен от собствена цена. */
  percent: number;
  currentTotal: number;
}

/** Продуктов ред в модала „Отстъпки" — продуктът заедно с добавките му. */
export interface DiscountRow {
  billElementItemId: number;
  name: string;
  quantity: number;
  /** Единичната цена за показване (продукт + добавки за една бройка): по
   *  менюто, а при „Промяна на цена" — собствената. */
  unitPrice: number;
  /** Продуктът + добавките — базата, върху която BE смята процента. */
  lines: PricedLine[];
  /** Сумата на реда в момента (с каквото вече е приложено). */
  currentTotal: number;
  /** Сумата на реда по менюто — без никаква корекция. */
  menuTotal: number;
  /** Активната отстъпка на реда; 0 при „Промяна на цена" или без отстъпка. */
  currentPercent: number;
  /** Процентът идва от „Промяна на цена" — отстъпката ще я замести. */
  hasCustomPrice: boolean;
  /** Заключена цена → редът не може да получи отстъпка. */
  locked: boolean;
  /** Имена на добавките („Кашкавал ×2") за подреда под продукта. */
  additionLabels: string[];
}

/** Какво ще стане с един ред при запис: редовете за заявката и сумата след тях. */
export interface RowDiscountPlan {
  lines: ItemDiscountLineIn[];
  total: number;
}

/** Half-up до стотинка — същото правило като BE (`ROUND_HALF_UP`). */
export const roundToCent = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100;

/** Количествата в BE са `Numeric(…, 3)`. */
const roundQuantity = (value: number): number =>
  Math.round((value + Number.EPSILON) * 1000) / 1000;

/** Сумата на ЕДИН ценови ред така, както я смятат BE
 *  (`service_item_pricing.row_total`) и фискалното устройство. Отстъпка:
 *  брутото се закръгля ПЪРВО, а процентът се смята от закръгленото бруто —
 *  едно закръгление накрая дава 9,32 вместо 9,31 при 3,45 × 3 с 10 %.
 *  Собствена цена и НАДБАВКА (отрицателен процент, BE 0.0.287): крайната
 *  единична цена × количество, закръглено веднъж — надбавката не се печата,
 *  редът отива на бона с по-високата цена (3,45 × 3 с +10 % = 3,80 × 3 = 11,40). */
export const lineTotal = (
  price: number,
  quantity: number,
  percent: number,
  ownPrice: boolean,
): number => {
  if (ownPrice || percent < 0) {
    const chargedUnitPrice = roundToCent((price * (100 - percent)) / 100);
    return roundToCent(chargedUnitPrice * quantity);
  }
  const gross = roundToCent(price * quantity);
  if (percent === 0) return gross;
  const delta = roundToCent((gross * Math.abs(percent)) / 100);
  return roundToCent(percent > 0 ? gross - delta : gross + delta);
};

/** Сумата на ЦЕЛИЯ ред (продукт + добавки) с един процент. */
export const totalAtPercent = (row: DiscountRow, percent: number): number =>
  row.lines.reduce(
    (sum, line) => sum + lineTotal(line.price, line.quantity, percent, false),
    0,
  );

/** Сумата на реда, ако `partQty` бройки се отделят с `percent`. Остатъкът
 *  или си остава както е (`restKeepsCurrent`), или минава без отстъпка.
 *  Добавките се делят пропорционално, до 0.001 — като `_split_item_quantity`. */
const splitTotal = (
  row: DiscountRow,
  partQty: number,
  percent: number,
  restKeepsCurrent: boolean,
): number =>
  row.lines.reduce((sum, line, index) => {
    const partQuantity =
      index === 0
        ? partQty
        : roundQuantity((line.quantity * partQty) / row.quantity);
    const restQuantity = roundQuantity(line.quantity - partQuantity);
    const partTotal = lineTotal(line.price, partQuantity, percent, false);
    const restTotal = restKeepsCurrent
      ? lineTotal(line.price, restQuantity, line.percent, row.hasCustomPrice)
      : lineTotal(line.price, restQuantity, 0, false);
    return sum + partTotal + restTotal;
  }, 0);

/** Модалът показва КРАЙНОТО състояние на реда: `selectedQty` бройки с
 *  `percent`, а останалите — без отстъпка. Оттук — какво да се прати.
 *  Ред без отстъпка и без избор (вкл. „Промяна на цена") не се пипа;
 *  размаркиран ред с отстъпка я губи. */
export const planRowDiscount = (
  row: DiscountRow,
  selectedQty: number,
  percent: number,
): RowDiscountPlan => {
  const id = row.billElementItemId;
  const current = row.currentPercent;
  const unchanged: RowDiscountPlan = { lines: [], total: row.currentTotal };
  const wholeRow = (targetPercent: number): RowDiscountPlan => ({
    lines: [
      { billElementItemId: id, quantity: row.quantity, percent: targetPercent },
    ],
    total: totalAtPercent(row, targetPercent),
  });

  if (row.locked || row.quantity <= 0) return unchanged;
  if (selectedQty <= 0 || percent === 0) {
    return current > 0 ? wholeRow(0) : unchanged;
  }
  if (selectedQty >= row.quantity) {
    return percent === current ? unchanged : wholeRow(percent);
  }

  // Част от реда — BE го разделя: отделените бройки получават процента.
  const restQty = roundQuantity(row.quantity - selectedQty);
  if (current === 0) {
    // Остатъкът си остава както е — по менюто или със собствената цена.
    return {
      lines: [{ billElementItemId: id, quantity: selectedQty, percent }],
      total: splitTotal(row, selectedQty, percent, true),
    };
  }
  if (percent === current) {
    // Избраните си пазят процента — отделя се само остатъкът, без отстъпка.
    return {
      lines: [{ billElementItemId: id, quantity: restQty, percent: 0 }],
      total: splitTotal(row, selectedQty, percent, false),
    };
  }
  // Два реда за един продукт: първият отделя избраните с новия процент,
  // вторият сваля остатъка (оригиналния ред) на 0. BE и касата ги прилагат
  // ПОРЕД върху една и съща сметка в паметта.
  return {
    lines: [
      { billElementItemId: id, quantity: selectedQty, percent },
      { billElementItemId: id, quantity: restQty, percent: 0 },
    ],
    total: splitTotal(row, selectedQty, percent, false),
  };
};

/** Най-често срещаният процент сред редовете с отстъпка (при равенство —
 *  първият срещнат); null без такива редове. */
export const mostCommonPercent = (rows: DiscountRow[]): number | null => {
  const counts = new Map<number, number>();
  for (const row of rows) {
    if (row.currentPercent > 0)
      counts.set(row.currentPercent, (counts.get(row.currentPercent) ?? 0) + 1);
  }
  let best: number | null = null;
  let bestCount = 0;
  for (const [percent, count] of counts) {
    if (count > bestCount) {
      best = percent;
      bestCount = count;
    }
  }
  return best;
};

const parseDecimal = (value: string): number => parseFloat(value) || 0;

const pickName = (
  itemNames: Record<string, string>,
  language: string,
  fallback: string,
): string =>
  itemNames?.[language] ||
  itemNames?.bg ||
  Object.values(itemNames || {})[0] ||
  fallback;

export const formatQuantity = (quantity: number): string =>
  quantity % 1 === 0 ? quantity.toFixed(0) : quantity.toFixed(3);

/** Редовете на сметката за модала (сторнираните не идват от BE). */
export const buildDiscountRows = (
  bill: BillOut | undefined,
  language: string,
  fallbackName: string,
): DiscountRow[] => {
  if (!bill) return [];
  return bill.elements.flatMap((element) =>
    element.items.map((item): DiscountRow => {
      const percent = parseDecimal(item.percent);
      const quantity = parseDecimal(item.quantity);
      const lines: PricedLine[] = [
        {
          price: parseDecimal(item.price),
          quantity,
          percent,
          currentTotal: parseDecimal(item.total_sum),
        },
        ...item.additions.map((addition) => ({
          price: parseDecimal(addition.price),
          quantity: parseDecimal(addition.quantity),
          percent: parseDecimal(addition.percent),
          currentTotal: parseDecimal(addition.total_sum),
        })),
      ];
      const currentTotal = lines.reduce((sum, line) => sum + line.currentTotal, 0);
      const menuTotal = lines.reduce(
        (sum, line) => sum + lineTotal(line.price, line.quantity, 0, false),
        0,
      );
      // Цената до името е по менюто — процентът в chip-а е спрямо нея; само
      // собствената цена се показва такава, каквато е.
      const shownTotal = item.custom_price ? currentTotal : menuTotal;
      return {
        billElementItemId: item.id,
        name: pickName(item.item_names, language, fallbackName),
        quantity,
        unitPrice: quantity > 0 ? shownTotal / quantity : 0,
        lines,
        currentTotal,
        menuTotal,
        // Отрицателен процент е надбавка от „Промяна на цена", не отстъпка.
        currentPercent: !item.custom_price && percent > 0 ? percent : 0,
        hasCustomPrice: item.custom_price,
        locked: item.locked_price,
        additionLabels: item.additions.map(
          (addition) =>
            `${pickName(addition.item_names, language, fallbackName)} ×${formatQuantity(
              parseDecimal(addition.quantity),
            )}`,
        ),
      };
    }),
  );
};
